import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Zap, MessageSquare, ListChecks, GitBranch, Bell, RefreshCw, Trash2, Save,
  Plus, Link2, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";

const NODE_TYPES = [
  { type: "trigger", label: "Trigger", icon: Zap, color: "#7C3AED", bg: "#F5F3FF" },
  { type: "whatsapp", label: "Send WhatsApp", icon: MessageSquare, color: "#059669", bg: "#ECFDF5" },
  { type: "task", label: "Add Task", icon: ListChecks, color: "#DB2777", bg: "#FDF2F8" },
  { type: "condition", label: "Check Condition", icon: GitBranch, color: "#6366F1", bg: "#EFF6FF" },
  { type: "notify", label: "Send Notification", icon: Bell, color: "#EA580C", bg: "#FFF7ED" },
  { type: "update", label: "Update Field", icon: RefreshCw, color: "#0891B2", bg: "#ECFEFF" },
];
const NW = 190, NH = 76;

export default function FlowchartBuilder() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [name, setName] = useState("management_seat_flow");
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [saved, setSaved] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const drag = useRef(null);
  const [connectFrom, setConnectFrom] = useState(null);

  const loadSaved = () => api.get("/flowcharts").then(({ data }) => setSaved(data));
  useEffect(() => { loadSaved(); }, []);

  const addNode = (t) => {
    const meta = NODE_TYPES.find((n) => n.type === t);
    const id = Math.random().toString(36).slice(2, 8);
    setNodes((ns) => [...ns, { id, type: t, label: meta.label, x: 60 + (ns.length % 4) * 220, y: 60 + Math.floor(ns.length / 4) * 130 }]);
  };

  const onMouseDown = (e, id) => {
    if (connectFrom) return;
    const n = nodes.find((x) => x.id === id);
    const rect = canvasRef.current.getBoundingClientRect();
    drag.current = { id, dx: e.clientX - rect.left - n.x, dy: e.clientY - rect.top - n.y };
  };
  const onMouseMove = useCallback((e) => {
    if (!drag.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(0, e.clientX - rect.left - drag.current.dx);
    const y = Math.max(0, e.clientY - rect.top - drag.current.dy);
    setNodes((ns) => ns.map((n) => (n.id === drag.current.id ? { ...n, x, y } : n)));
  }, []);
  const onMouseUp = () => { drag.current = null; };

  const nodeClick = (id) => {
    if (connectFrom && connectFrom !== id) {
      setEdges((es) => (es.some((ed) => ed.from === connectFrom && ed.to === id) ? es : [...es, { from: connectFrom, to: id }]));
      setConnectFrom(null);
    }
  };
  const removeNode = (id) => { setNodes((ns) => ns.filter((n) => n.id !== id)); setEdges((es) => es.filter((e) => e.from !== id && e.to !== id)); };

  const save = async () => {
    if (currentId) { await api.patch(`/flowcharts/${currentId}`, { name, nodes, edges }); toast.success("Flow updated"); }
    else { const { data } = await api.post("/flowcharts", { name, nodes, edges }); setCurrentId(data.id); toast.success("Flow saved"); }
    loadSaved();
  };
  const openFlow = (f) => { setCurrentId(f.id); setName(f.name); setNodes(f.nodes || []); setEdges(f.edges || []); };
  const newFlow = () => { setCurrentId(null); setName("new_workflow"); setNodes([]); setEdges([]); };

  const center = (n) => ({ x: n.x + NW / 2, y: n.y + NH / 2 });

  return (
    <div>
      <button onClick={() => navigate("/automation")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#312E81] mb-3"><ArrowLeft className="w-4 h-4" /> Back to Workflows</button>
      <PageHeader title="Flow Builder" subtitle="Drag nodes, connect them, and publish your admission automation" testid="flow-header">
        <Input value={name} onChange={(e) => setName(e.target.value)} className="w-52 bg-white" data-testid="flow-name" />
        <Button variant="outline" onClick={newFlow} data-testid="flow-new"><Plus className="w-4 h-4 mr-1" /> New</Button>
        <Button onClick={save} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="flow-save"><Save className="w-4 h-4 mr-1" /> Save & Publish</Button>
      </PageHeader>

      <div className="grid lg:grid-cols-[220px_1fr] gap-4">
        {/* palette + saved */}
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Add Node</div>
            <div className="space-y-1.5">
              {NODE_TYPES.map((n) => {
                const Icon = n.icon;
                return (
                  <button key={n.type} onClick={() => addNode(n.type)} data-testid={`palette-${n.type}`}
                    className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm font-medium hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-colors"
                    style={{ color: n.color }}>
                    <Icon className="w-4 h-4" /> {n.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Saved Flows ({saved.length})</div>
            <div className="space-y-1">
              {saved.map((f) => (
                <button key={f.id} onClick={() => openFlow(f)} data-testid="saved-flow"
                  className={`w-full text-left px-2.5 py-2 rounded-lg text-sm truncate ${currentId === f.id ? "bg-[#312E81] text-white" : "hover:bg-slate-50 text-slate-600"}`}>
                  {f.name}
                </button>
              ))}
              {saved.length === 0 && <div className="text-xs text-slate-400 px-1">No saved flows</div>}
            </div>
          </div>
          {connectFrom && <div className="text-xs bg-amber-50 border border-amber-200 rounded-lg p-2 text-amber-700">Click a target node to connect, or click here to cancel.
            <button onClick={() => setConnectFrom(null)} className="underline ml-1">cancel</button></div>}
        </div>

        {/* canvas */}
        <div ref={canvasRef} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
          data-testid="flow-canvas"
          className="relative bg-white border border-slate-200 rounded-xl shadow-sm overflow-auto"
          style={{ height: "70vh", backgroundImage: "radial-gradient(#e2e8f0 1px, transparent 1px)", backgroundSize: "18px 18px" }}>
          <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ minHeight: "100%" }}>
            {edges.map((e, i) => {
              const a = nodes.find((n) => n.id === e.from), b = nodes.find((n) => n.id === e.to);
              if (!a || !b) return null;
              const p1 = center(a), p2 = center(b);
              const mx = (p1.x + p2.x) / 2;
              return <path key={i} d={`M ${p1.x} ${p1.y} C ${mx} ${p1.y}, ${mx} ${p2.y}, ${p2.x} ${p2.y}`} stroke="#94a3b8" strokeWidth="2" fill="none" markerEnd="url(#arrow)" />;
            })}
            <defs><marker id="arrow" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto" markerUnits="strokeWidth"><path d="M0,0 L0,6 L9,3 z" fill="#94a3b8" /></marker></defs>
          </svg>

          {nodes.length === 0 && <div className="absolute inset-0 flex items-center justify-center text-slate-300 text-sm pointer-events-none">Add nodes from the left palette, then drag & connect them</div>}

          {nodes.map((n) => {
            const meta = NODE_TYPES.find((t) => t.type === n.type);
            const Icon = meta.icon;
            return (
              <div key={n.id} onMouseDown={(e) => onMouseDown(e, n.id)} onClick={() => nodeClick(n.id)}
                data-testid={`node-${n.id}`}
                className="absolute rounded-xl shadow-md border cursor-move select-none group"
                style={{ left: n.x, top: n.y, width: NW, minHeight: NH, background: meta.bg, borderColor: meta.color }}>
                <div className="flex items-center gap-2 px-3 py-2 rounded-t-xl text-white text-xs font-semibold" style={{ background: meta.color }}>
                  <Icon className="w-3.5 h-3.5" /> {meta.label}
                  <button onClick={(e) => { e.stopPropagation(); removeNode(n.id); }} className="ml-auto opacity-0 group-hover:opacity-100"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <div className="px-3 py-2 text-[11px] text-slate-600">{n.type === "trigger" ? "On lead event" : n.type === "condition" ? "If course = …" : "Configure action"}</div>
                <button onClick={(e) => { e.stopPropagation(); setConnectFrom(n.id); }}
                  data-testid={`connect-${n.id}`}
                  className="absolute -right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-white border-2 flex items-center justify-center hover:scale-110 transition-transform"
                  style={{ borderColor: meta.color, color: meta.color }} title="Connect">
                  <Link2 className="w-2.5 h-2.5" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
